import { ChangeEvent, FC, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Typography,
  CardHeader,
  TextField,
  Grid,
  Button,
  InputAdornment,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RemoveIcon from '@mui/icons-material/Remove';
import FilterListIcon from '@mui/icons-material/FilterList';
import styled from '@emotion/styled';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { useNavigate } from 'react-router';
import { withStyles } from '@mui/styles';
import Rating from '@mui/material/Rating';
import CircleIcon from '@mui/icons-material/Circle';
import { TableColumnCell } from 'src/components/TableColumnCell';
import AddIcon from '@mui/icons-material/Add';
import AllocateModal from './components/AllocateModal';
import DeAllocateModal from './components/DeAllocateModal';
import { useForm } from 'react-hook-form';
import {
  filterResource,
  getProjectTotalAllocation,
  searchResource
} from 'src/services/projectService';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AllocationsChartModal from './AllocationsChartModal';
import { getAllBillingRoles } from 'src/services/employeeService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';
import {
  getRolePermissions
} from 'src/services/authService';
import { validateMultiRolesAndPermissions } from 'src/utils/validate';

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#1bc47d'
  },
  '& .MuiRating-iconHover': {
    color: '#1bc47d'
  }
});

interface SrTableProps {}

interface Filters {
  status?: any;
}

const applyFilters = (shadowList: any, filters: Filters): any => {
  return shadowList.filter((shadowStatus) => {
    let matches = true;
    return matches;
  });
};

const applyPagination = (shadowList: any, page: number, limit: number): any => {
  return shadowList?.slice(page * limit, page * limit + limit);
};

const ShadowResourceTable: FC<SrTableProps> = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenDeAllocate, setIsOpenDeAllocate] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  var [shadowList, setShadowList] = useState<any>([]);
  const [filters] = useState<Filters>({ status: null });
  const [toggleFilter, setToggleFilter] = useState(false);
  const [yearsExperience, setYearsExperience] = useState([]);
  const navigate = useNavigate();
   
  const [permissions, setPermissions] = useState([]);

  const getPermissions = (mail) => {
    getRolePermissions(mail).then((res) => {
      const tabs = validateMultiRolesAndPermissions(res);
      const arr = tabs.filter((fil) => fil.name === 'Resource Management');
      setPermissions(arr[0].permissions);
    });
  };

  useEffect(() => {
    if (shadowList.length > 0)
      setYearsExperience([
        ...new Set(shadowList.map((shad) => shad.experience))
      ]);
  }, [shadowList]);

  const CardHeaderWrapper = styled(CardHeader)(
    () => `
    .MuiCardHeader-action{
      width: 100%
    }
  `
  );

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const [checkedUsers, setCheckedUsers] = useState<any>([]);
  const [deallocateUsers, setDeallocateUsers] = useState([]);

  const toggleDealocate = async () => {
    setIsOpenDeAllocate(!isOpenDeAllocate);
  };

  const getTableData = () => {
    dispatch(AppActions.showLoader());

    setCheckedUsers([]);
    setDeallocateUsers([]);
    getProjectTotalAllocation().then((res) => {
      setShadowList((res.data.reverse() as any) || []);
      dispatch(AppActions.hideLoader());
    });
  };

  useEffect(() => {
    getTableData();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (Object.keys(user).length === 0) {
      navigate('/');
    } else {
      getPermissions(user.Email);
    }
  }, []);

  // custom typography with different text and cursor pointer
  const PrimaryTextTypography = withStyles({
    root: {
      color: '#0096FF',
      cursor: 'pointer'
    }
  })(Typography);

  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  const [filteredShadow, setfilteredShadow] = useState<any>(
    applyFilters(shadowList, filters)
  );
  const [paginatedShadow, setpaginatedShadow] = useState<any>(
    applyPagination(filteredShadow, page, limit)
  );

  const [sortIdType, setSortIdType] = useState('asc');
  const [sortNameType, setSortNameType] = useState('asc');
  const [sortNameJiraType, setSortNameJiraType] = useState('asc');

  useEffect(() => {
    setfilteredShadow(applyFilters(shadowList, filters));
  }, [
    shadowList,
    page,
    limit,
    filters,
    sortIdType,
    sortNameType,
    sortNameJiraType
  ]);

  useEffect(() => {
    setpaginatedShadow(applyPagination(filteredShadow, page, limit));
  }, [shadowList, page, limit, filteredShadow]);

  const [searchKeywords, setSearchKeywords] = useState('');

  //when user started to type the name in the text field
  const nameChange = (e) => {
    setSearchKeywords(e.target.value);
  };

  const searchShadowsAll = async (keyword: string) => {
    dispatch(AppActions.showLoader());
    const result = await searchResource(keyword);
    setPage(0);
    setShadowList(result?.data);
    dispatch(AppActions.hideLoader());
  };

  useEffect(() => {
    if (searchKeywords.length > 0) {
      searchShadowsAll(searchKeywords);
    }
    if (searchKeywords.length === 0) {
      getTableData();
    }
  }, [searchKeywords]);

  let initialProjectFilterValues = {
    designation: null,
    experience: null,
    totalAllocation: null
  } as any;

  // removed get values
  const { register, handleSubmit, reset, setValue } = useForm<any>({
    defaultValues: initialProjectFilterValues
  });
  const [isDisabled, setIsDisabled] = useState(false);

  const [empFilter, setEmpFilter] = useState({} as any);

  const onSubmit = (data: any) => {
    dispatch(AppActions.showLoader());
    filterResource(data).then((res) => {
      setPage(0);
      setShadowList((res.data as any[]) || []);
      dispatch(AppActions.hideLoader());
    });
  };

  const resetFilterResult = () => {
    reset();
    setEmpFilter(initialProjectFilterValues);
    getTableData();
  };

  useEffect(() => {
    const isNullish = Object.values(empFilter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(empFilter).length === 0 || isNullish) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [empFilter]);

  const [isOpenChart, setIsOpenChart] = useState(false);
  const [chartData, setChartData] = useState<any>({});
  const [chartName, setChartName] = useState<any>('');
  const [chatAllocation, setAllocation] = useState<any>('');

  const toggleChart = () => {
    setIsOpenChart(!isOpenChart);
  };

  const [billingRoles, setBillingRoles] = useState<any>([]);

  useEffect(() => {
    getAllBillingRoles().then((res) => setBillingRoles(res.data));
  }, []);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  return (
    <>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Resource Management
          </Typography>
          <Typography variant="subtitle2"></Typography>
        </Grid>
        <Grid item>
        {permissions.some(
                (perm) =>
                  perm.name === 'Edit Resources' && perm.value === true
              ) && 
          <>
            <Button
            onClick={() => toggleDealocate()}
            disabled={checkedUsers.length === 0}
            sx={{
              mt: { xs: 2, md: 0 },
              fontSize: '11px',
              fontWeight: 'normal'
            }}
            variant="contained"
            startIcon={<RemoveIcon fontSize="small" />}
            style={{ height: '1.75rem', marginRight: '1rem' }}
          >
            Deallocate
          </Button>
          <Button
            disabled={checkedUsers.length > 0}
            onClick={() => toggle()}
            sx={{
              mt: { xs: 2, md: 0 },
              fontSize: '11px',
              fontWeight: 'normal'
            }}
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            style={{ height: '1.75rem' }}
          >
            Allocate
          </Button>
          </>
        }
        </Grid>
      </Grid>
      <Card>
        <Grid
          container
          style={{
            justifyContent: 'space-between',
            marginBottom: 7,
            padding: '1rem'
          }}
          spacing={2}
        >
          <Grid item xs={6}>
            <TextField
              sx={{ width: 150, mr: 2 }}
              size="small"
              placeholder="Search..."
              onChange={(e) => nameChange(e)}
              // {...register('name', {onChange: (e) => {nameChange(e)}})}
              InputProps={{
                style: { fontSize: '12px', height: '1.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: '#d3d3d3' }}
                      style={{ fontSize: '15px' }}
                    />
                  </InputAdornment>
                )
              }}
            />
            <Button
              sx={{
                mt: { xs: 2, md: 0 },
                color: `${!toggleFilter && 'gray'}`
              }}
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                setToggleFilter(!toggleFilter);
              }}
              style={{ height: '1.75rem', fontSize: '12px' }}
            >
              Filters
            </Button>
          </Grid>
        </Grid>
        <CardHeaderWrapper
          style={{ padding: 0 }}
          action={
            <>
              <form onSubmit={handleSubmit(onSubmit)}>
                {toggleFilter ? (
                  <>
                    <Grid
                      container
                      spacing={2}
                      style={{
                        paddingTop: 3,
                        paddingBottom: 2,
                        paddingLeft: '0.5rem'
                      }}
                    >
                      <Grid item xs={3} sx={{ p: 0 }}>
                        <FormControl fullWidth>
                          <InputLabel
                            style={{
                              fontSize: '12px'
                            }}
                          >
                            Designation
                          </InputLabel>
                          <Select
                            sx={{ mt: 1 }}
                            style={{
                              fontSize: '12px',
                              height: '2rem'
                            }}
                            autoWidth
                            size="small"
                            {...register('designation')}
                            value={empFilter.designation}
                            onChange={(e) => {
                              setEmpFilter((prev) => ({
                                ...prev,
                                designation: e.target.value
                              }));
                              setValue('designation', e.target.value);
                            }}
                          >
                            {(billingRoles || []).map((item, i) => {
                              return (
                                <MenuItem value={item.name} key={i}>
                                  {item.name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel
                            style={{
                              fontSize: '12px'
                            }}
                          >
                            Years of Experience
                          </InputLabel>
                          <Select
                            sx={{ mt: 1 }}
                            style={{
                              fontSize: '12px',
                              height: '2rem'
                            }}
                            autoWidth
                            size="small"
                            value={empFilter.experience}
                            {...register('experience')}
                            onChange={(e) => {
                              setEmpFilter((prev) => ({
                                ...prev,
                                experience: e.target.value
                              }));
                              setValue('experience', e.target.value);
                            }}
                          >
                            {(yearsExperience || []).map((item, i) => {
                              return (
                                <MenuItem value={item || 0} key={i}>
                                  {item || 0}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl fullWidth>
                          <InputLabel
                            style={{
                              fontSize: '12px'
                            }}
                          >
                            Total Allocation
                          </InputLabel>
                          <Select
                            className="select-text"
                            style={{
                              fontSize: '12px',
                              height: '2rem'
                            }}
                            sx={{ mt: 1 }}
                            autoWidth
                            size="small"
                            value={empFilter.totalAllocation || 0}
                            {...register('totalAllocation')}
                            onChange={(e) => {
                              setEmpFilter((prev) => ({
                                ...prev,
                                totalAllocation: e.target.value
                              }));
                              setValue('totalAllocation', e.target.value);
                            }}
                          >
                            <MenuItem value={'0'}>{'Not Allocated'}</MenuItem>
                            <MenuItem value={'10'}>{'10'}</MenuItem>
                            <MenuItem value={'20'}>{'20'}</MenuItem>
                            <MenuItem value={'30'}>{'30'}</MenuItem>
                            <MenuItem value={'40'}>{'40'}</MenuItem>
                            <MenuItem value={'50'}>{'50'}</MenuItem>
                            <MenuItem value={'60'}>{'60'}</MenuItem>
                            <MenuItem value={'70'}>{'70'}</MenuItem>
                            <MenuItem value={'80'}>{'80'}</MenuItem>
                            <MenuItem value={'90'}>{'90'}</MenuItem>
                            <MenuItem value={'100'}>{'100'}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid
                      sx={{ pr: 2 }}
                      container
                      style={{
                        justifyContent: 'flex-end',
                        marginTop: 4,
                        marginBottom: '1.5rem'
                      }}
                      xs={12}
                    >
                      <Button
                        sx={{ mt: { xs: 2, md: 0 }, mr: 2 }}
                        variant="text"
                        onClick={resetFilterResult}
                        disabled={isDisabled}
                        className={isDisabled &&'clear-btn'}
                        style={{ height: '1.75rem', fontSize: '12px' }}
                      >
                        Clear
                      </Button>
                      <Button
                        sx={{ mt: { xs: 2, md: 0 } }}
                        disabled={isDisabled}
                        variant="outlined"
                        type="submit"
                        className={isDisabled &&'filter-btn'}
                        style={{ height: '1.75rem', fontSize: '12px' }}
                      >
                        Filter
                      </Button>
                    </Grid>
                  </>
                ) : (
                  <></>
                )}
              </form>
            </>
          }
          title=""
        />
        <TableContainer>
          <Table style={{ borderCollapse: 'separate' }} size="small">
            <TableHead sx={{ backgroundColor: '#e6e9ff' }}>
              <CustomTableRow sx={{ py: 10 }}>
                <TableHeaderCell content=" " smallFont />
                <TableHeaderCell
                  content="Employee Name"
                  smallFont
                  sort
                  setSortType={setSortNameJiraType}
                  sortType={sortNameJiraType}
                />
                <TableHeaderCell content="Designation" smallFont />
                <TableHeaderCell content="Experience" smallFont />
                <TableHeaderCell content="Skills" smallFont />
                <TableHeaderCell content="Total Allocation" smallFont />
                <TableHeaderCell content="Allocation Summary" smallFont />
                <TableHeaderCell content="Actions" smallFont sticky />
              </CustomTableRow>
            </TableHead>
            <TableBody>

            {permissions.some(
                                (perm) =>
                                  perm.name === 'View Resources' && perm.value === true
                              ) && 

                <>
              {paginatedShadow &&
                paginatedShadow?.length > 0 &&
                paginatedShadow.map((shadow) => (
                  <TableRow style={{ alignItems: 'start' }} hover>
                    <TableColumnCell>
                      <Checkbox
                        checked={checkedUsers.includes(shadow.employeeId)}
                        color="primary"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setDeallocateUsers([...deallocateUsers, shadow]);
                            setCheckedUsers([
                              ...checkedUsers,
                              shadow.employeeId
                            ]);
                          } else {
                            setCheckedUsers(
                              checkedUsers.filter(
                                (fil) => fil !== shadow.employeeId
                              )
                            );
                          }
                        }}
                      />
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: '11px' }}
                        gutterBottom
                        noWrap
                      >
                        {shadow.name}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px' }}
                        gutterBottom
                        noWrap
                      >
                        {shadow.designation}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px' }}
                        gutterBottom
                        noWrap
                      >
                        {shadow.yearsOfExperience || 0}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      {shadow?.skills?.map((skill) => (
                        <Grid
                          display="flex"
                          style={{ marginTop: '0.75rem' }}
                          alignItems="center"
                          item
                          xs={12}
                        >
                          <Grid item xs={6}>
                            <Typography
                              variant="body1"
                              style={{ fontSize: '11px' }}
                              color="text.primary"
                              gutterBottom
                              noWrap
                            >
                              {skill.name}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <StyledRating
                              readOnly
                              name="customized-color"
                              value={skill.level}
                              precision={1}
                              icon={<CircleIcon style={{ fontSize: '10px' }} />}
                              emptyIcon={
                                <CircleIcon style={{ fontSize: '10px' }} />
                              }
                            />
                          </Grid>
                        </Grid>
                      ))}
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px' }}
                        gutterBottom
                        noWrap
                      >
                        {`${shadow.totalAllocation}%`}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <PrimaryTextTypography
                        onClick={() => {
                          setChartData(shadow.allocations);
                          setChartName(shadow.name);
                          setAllocation(shadow.totalAllocation)
                          toggleChart();
                        }}
                        variant="body2"
                        style={{ fontSize: '11px' }}
                        gutterBottom
                        noWrap
                      >
                        View
                      </PrimaryTextTypography>
                    </TableColumnCell>
                    <TableColumnCell sticky>
                      <div
                        style={{
                          background: 'white',
                          marginLeft: 'auto'
                        }}
                      >
                          {permissions.some(
                                (perm) =>
                                  perm.name === 'Edit Resources' && perm.value === true
                              ) && 
                            <>
                                <Tooltip title="Edit" placement="top" >
                                <EditIcon
                                  onClick={() =>
                                    navigate(
                                      `resource-edit/${shadow.employeeId}?view=false`
                                    )
                                  }
                                  style={{
                                    fontSize: '17px',
                                    cursor: 'pointer'
                                  }}
                                />
                                </Tooltip>
                            </>
                          }

                        <Tooltip title="View" placement="top" >
                        <VisibilityIcon
                          onClick={() =>
                            navigate(
                              `resource-edit/${shadow.employeeId}?view=true`
                            )
                          }
                          style={{
                            fontSize: '17px',
                            cursor: 'pointer'
                          }}
                        />
                        </Tooltip>
                      </div>
                    </TableColumnCell>
                  </TableRow>
                ))}
                </>
            }
            </TableBody>
          </Table>
        </TableContainer>

        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={filteredShadow?.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <AllocateModal
        isOpen={isOpen}
        toggle={toggle}
        getData={getTableData}
        singlePage={null}
        setSnackBarOpen={setSnackBarOpen}
        setSnackBarMessage={setSnackBarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
      />
      <DeAllocateModal
        isOpen={isOpenDeAllocate}
        toggle={toggleDealocate}
        deAllocateData={deallocateUsers}
        getData={getTableData}
        setSnackBarOpen={setSnackBarOpen}
        setSnackBarMessage={setSnackBarMessage}
        setSnackbarSeverity={setSnackbarSeverity}
        setCheckedUsers={setCheckedUsers}
        setDeallocateUsers={setDeallocateUsers}
      />
      <AllocationsChartModal
        open={isOpenChart}
        toggle={toggleChart}
        data={chartData}
        name={chartName}
        allocation={chatAllocation}
      />
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
    </>
  );
};

ShadowResourceTable.propTypes = {};

ShadowResourceTable.defaultProps = {};

export default ShadowResourceTable;
